import React, { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import {
  Header,
  ActionPlaceholder,
  TriggerBlock,
  NameComponent,
  FilterBlock,
  ActionBlock,
  AddFilterActionBtn,
  AutomationRuleConflictMessage,
} from './components';
import { generateFilterPayload } from '../utils/generateFilterPayload';
import { generateActionPayload } from '../utils/generateActionPayload';
import { parseAutomationData } from '../utils/parseAutomationData';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import useAutomationBuilder from '../hooks/useAutomationBuilder';
import { setAppTitle } from 'utils/utils';
import { useUserDetailsContext } from 'context/userDetailsContext';

export default function WorkflowBuilder() {
  const methods = useForm();
  const history = useHistory();
  const { userDetails } = useUserDetailsContext();
  const { automationData, conflictErrorData, setConflictErrorData, createOrUpdateAutomation } = useAutomationBuilder();
  const filterMethods = useFieldArray({ control: methods.control, name: 'filters' });
  const actionMethods = useFieldArray({ control: methods.control, name: 'actions' });

  const trigger = methods.watch('trigger');
  const filters = methods.watch('filters');
  const actions = methods.watch('actions');

  useEffect(() => {
    if (automationData && userDetails?.timezone) {
      methods.reset(parseAutomationData(automationData, userDetails?.timezone));
    }
    setAppTitle('Automation', automationData?.workflow_title || '');
    return () => {
      setAppTitle();
    };
  }, [automationData, userDetails]);

  const submit = (formData) => {
    let validationData = {
      validation_action_code: formData?.actions?.map((v) => v?.action_code?.replace('ACT_', '')) || [],
      validation_trigger_code: formData?.trigger?.code?.replace('TRIG_', ''),
    };
    if (validationData?.validation_trigger_code === 'EVENT_CREATE' && validationData?.validation_action_code?.includes('EVENT_CREATE')) {
      validationData.validation_trigger_code = 'WORKFLOW_EVENT_CREATE';
    }
    let data = {
      automation_status: 'ACTIVE',
      is_lead_automation: formData?.is_lead_automation,
      practice_area: formData?.filters
        ?.filter((v) => v?.label === 'By Practice Area')
        ?.reduce((acc, v) => [...(v?.value?.['case_description.case_practice_area'] || []), ...acc], []),
      workflow_title: formData?.workflow_title,
      workflow_description: formData?.workflow_description,
      workflow: {
        fire_on: {
          label: formData?.trigger?.label,
          module: formData?.trigger?.module,
          trigger_code: formData?.trigger?.code,
        },
        filter: generateFilterPayload(formData?.filters),
        execution_sequence: generateActionPayload(formData?.actions, formData?.trigger?.code, userDetails?.timezone),
      },
      ...validationData,
      pk: formData?.pk,
      sk: formData?.sk,
    };
    if (formData?.trigger?.code === 'TRIG_PAYMENT_REQUEST_PASTDUE' || formData?.trigger?.code === 'TRIG_INVOICE_PASTDUE') {
      data.workflow.past_due_delay = formData?.past_due_delay;
    }
    if (formData?.trigger?.code === 'TRIG_REVIEW_REQUEST') {
      data.workflow.past_due_delay = `${formData?.count}#${formData?.period}`;
    }
    createOrUpdateAutomation(data, validationData);
  };

  const onSubmit = () => {
    if (automationData?.sk || automationData?.pk) {
      confirmDialog({
        header: 'Confirm Changes',
        message: 'All running instances will stop while updating. Do you want to proceed?',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-primary',
        rejectClassName: 'p-button-secondary outline',
        closable: false,
        accept: () => {
          methods.handleSubmit(submit)();
        },
      });
    } else {
      methods.handleSubmit(submit)();
    }
  };

  const onCancel = () => {
    confirmDialog({
      header: 'Confirm Cancel',
      message: 'Are you sure you want to cancel? All unsaved changes will be lost.',
      icon: 'pi pi-exclamation-triangle',
      rejectClassName: 'p-button-secondary outline',
      acceptClassName: 'p-button-primary',
      accept: () => {
        history.goBack();
      },
    });
  };

  return (
    <FormProvider {...methods} filterMethods={filterMethods} actionMethods={actionMethods}>
      <GlobalLoader />
      <Header />
      <div className="card-bg max-w-6xl mx-auto p-4 bg-gray-50 min-h-screen pb-3">
        <p className="sub-title text-gray-500 mt-1">Set up automated actions triggered by specific events</p>
        <NameComponent />
      </div>
      <div className="card-bg max-w-6xl mx-auto p-4 bg-gray-50 min-h-screen pb-3 mt-3">
        <TriggerBlock />
        {filters?.length > 0 && <FilterBlock />}
        {actions?.length > 0 && <ActionBlock />}
        {trigger && !actions?.length && (
          <AddFilterActionBtn showAddFilter={filters?.length === 0 && trigger?.code !== 'TRIG_REVIEW_REQUEST'} />
        )}
        {!trigger && <ActionPlaceholder />}
      </div>
      {trigger && (
        <div className="card-bg mt-2 p-3 d-flex justify-content-end gap-2" style={{ position: 'sticky', bottom: -30 }}>
          <Button className="p-button-secondary outline" onClick={onCancel}>
            Cancel
          </Button>
          {/* <Button className="p-button-primary" style={{ height: 32 }}>
            Save Draft
          </Button> */}
          <Button className="p-button-primary" disabled={actions?.length === 0} style={{ height: 32 }} onClick={onSubmit}>
            {automationData?.pk ? 'Update Workflow' : 'Create and Activate Workflow'}
          </Button>
        </div>
      )}
      {conflictErrorData && (
        <AutomationRuleConflictMessage ruleValidationErrorData={conflictErrorData} onHide={() => setConflictErrorData(null)} />
      )}
    </FormProvider>
  );
}
